// Chat API Service
import { ApiError, apiService, ChatMessage, ChatResponse } from "./api";
import { apiFetch } from "../utils/apiClient";

// Re-export types for convenience
export type { ChatMessage, ChatResponse };

/** The product surfaces a chat can be tagged with (DOP-437, DOP-450). */
export type ChatSurface =
	| "kpi_dashboard"
	| "monitoring_run"
	/** A deal-screening run's Analyst rail thread (DOP-499). */
	| "deal_screening_run"
	/** A loan tape dashboard: the thread IS the dashboard (DOP-477). */
	| "loan_tape"
	/** A loan tape's Analyst rail thread: a conversation about the tape. */
	| "loan_tape_run"
	/** The Analyst rail beside a deal's portfolio tables. */
	| "portfolio_data_run";

export interface ChatListItem {
	id: string;
	title: string;
	last_message_at: number | null;
	created_at: number;
	updated_at: number;
	has_active_stream?: boolean;
	/**
	 * What the newest generation on this thread did (DOP-477). Distinct from
	 * `has_active_stream`, which asks whether any generation is still open.
	 */
	latest_generation_status?: "pending" | "running" | "completed" | "failed" | null;
	/** Where the thread lives, when it is not an ordinary chat. */
	surface?: ChatSurface | string | null;
	/**
	 * The thread's last view on its surface: for the dashboard, the last
	 * turn's descriptor. Only the fields a link needs are read here.
	 */
	surface_state?: {
		scope?: { property_key?: string | null; rollup?: string | null } | null;
		grain?: string | null;
		/** A monitoring thread: the run it was asked about and the tab (DOP-470). */
		run_public_id?: string | null;
		/**
		 * A deal screening thread: the run its last turn was asked about. A loan
		 * tape thread: the run on screen then (a message id), null for the newest.
		 */
		run_id?: string | number | null;
		/** A loan tape thread: the tape it is about. */
		tape_id?: string | null;
		/** A loan tape thread: the Mapping view it was asked from, if any. */
		mapping_view?: string | null;
		/** A dashboard thread: the workflow it was asked from. */
		workflow_id?: string | null;
		/** A portfolio data thread: the deal whose tables it is about. */
		deal_name?: string | null;
		tab?: string | null;
	} | null;
}

/** A thread's surface and last view, as the messages endpoint reports them. */
export interface ChatHome {
	surface?: string | null;
	surface_state?: ChatListItem["surface_state"];
}

export interface ChatListResponse {
	chats: ChatListItem[];
	limit: number;
	next_cursor: string | null;
	has_more: boolean;
}

export interface ModifiedWorkbookSignedUrlResponse {
	signed_url: string;
	expires_at: string;
	filename: string;
}

export interface WorkbookArtifactItem {
	block_id: string;
	message_id: number;
	message_created_at_ms: number;
	filename: string;
	mime_type: string;
	output_hash: string;
	signed_url: string | null;
	hidden: boolean;
}

export interface WorkbookArtifactsResponse {
	items: WorkbookArtifactItem[];
}

class ChatApiService {
	async getModifiedWorkbookSignedUrl(
		chatId: string,
		blockId: string,
		expiresIn = 300,
	): Promise<ModifiedWorkbookSignedUrlResponse> {
		const base = apiService.getBaseUrl();
		const response = await apiFetch(
			`${base}/api/v3/chats/${chatId}/workbook-blocks/${blockId}/signed-url?expires_in=${expiresIn}`,
		);
		if (!response.ok) {
			throw new Error(`Re-mint failed: ${response.status}`);
		}
		return response.json();
	}

	async downloadWorkbookArtifact(
		chatId: string,
		blockId: string,
	): Promise<Blob> {
		const base = apiService.getBaseUrl();
		const response = await apiFetch(
			`${base}/api/v3/chats/${chatId}/workbook-blocks/${encodeURIComponent(
				blockId,
			)}/download`,
		);
		if (!response.ok) {
			throw new Error(
				`Failed to download workbook artifact: ${response.status}`,
			);
		}
		return response.blob();
	}

	async listWorkbookArtifacts(
		chatId: string,
	): Promise<WorkbookArtifactsResponse> {
		const base = apiService.getBaseUrl();
		const response = await apiFetch(
			`${base}/api/v3/chats/${chatId}/workbook-artifacts`,
		);
		if (!response.ok) {
			if (response.status === 413) {
				throw new Error(
					"Too many workbooks in this chat to bundle into one zip.",
				);
			}
			throw new Error(`Failed to list workbook artifacts: ${response.status}`);
		}
		return response.json();
	}

	async renameChat(chatId: string, title: string): Promise<void> {
		const base = apiService.getBaseUrl();
		const response = await apiFetch(`${base}/api/v3/chats/${chatId}`, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ title }),
		});
		if (!response.ok) {
			throw new Error(`Failed to rename chat: ${response.status}`);
		}
	}

	async deleteChat(chatId: string): Promise<void> {
		const base = apiService.getBaseUrl();
		const response = await apiFetch(`${base}/api/v3/chats/${chatId}`, {
			method: "DELETE",
		});
		if (!response.ok) {
			// `ApiError` rather than a bare `Error`: a caller has to be able to
			// tell a refusal it can explain (409 — a run is in flight) from one
			// it cannot. Same message, so anything reading `.message` is
			// unaffected.
			throw new ApiError(
				response.status,
				`Failed to delete chat: ${response.status}`,
			);
		}
	}

	/**
	 * Where one thread lives: its surface and the last view asked from. Read
	 * off the messages endpoint's own chat summary, one message deep, so a rail
	 * can tell whether a thread it is asked to open is one of its own.
	 */
	async getChatHome(chatId: string): Promise<ChatHome> {
		const raw = await apiService.get<{ chat?: ChatHome }>(
			`/api/v3/chats/${encodeURIComponent(chatId)}/messages?limit=1`,
		);
		return {
			surface: raw.chat?.surface ?? null,
			surface_state: raw.chat?.surface_state ?? null,
		};
	}

	async listChats(params?: {
		limit?: number;
		cursor?: string | null;
		surface?: ChatSurface;
		/** With `loan_tape_run`: only the threads about this tape. */
		tape_id?: string;
	}): Promise<ChatListResponse> {
		const query = new URLSearchParams();
		if (typeof params?.limit === "number") {
			query.set("limit", String(params.limit));
		}
		if (params?.cursor) {
			query.set("cursor", params.cursor);
		}
		if (params?.surface) {
			query.set("surface", params.surface);
		}
		if (params?.tape_id) {
			query.set("tape_id", params.tape_id);
		}

		const queryString = query.toString();
		const endpoint = queryString
			? `/api/v3/chats?${queryString}`
			: "/api/v3/chats";

		return apiService.get<ChatListResponse>(endpoint);
	}

	// Real streaming response
	async streamChat(
		messages: ChatMessage[]
	): Promise<ReadableStream<ChatResponse>> {
		try {
			// Use the API service for streaming
			const stream = await apiService.stream("/api/v1/chat/stream", {
				messages,
			});
			return stream as ReadableStream<ChatResponse>;
		} catch (error) {
			console.error("Error with real API, falling back to mock:", error);

			// Fallback to mock response if API fails
			const lastMessage = messages[messages.length - 1];
			const mockResponses = [
				`I understand you're asking about "${lastMessage.content}". Let me help you with that. This is a simulated streaming response to demonstrate the real-time chat functionality.`,
				`Based on your question "${lastMessage.content}", here are some insights: This mock response shows how the AI would stream its answer in real-time, just like ChatGPT.`,
				`Great question about "${lastMessage.content}"! Here's what I can tell you: This is a demonstration of streaming text that appears character by character, simulating the experience of chatting with an AI agent.`,
			];

			const randomResponse =
				mockResponses[Math.floor(Math.random() * mockResponses.length)];

			// Create a readable stream that simulates AI streaming
			return new ReadableStream({
				start(controller) {
					let index = 0;

					const streamNext = () => {
						if (index < randomResponse.length) {
							controller.enqueue({
								content: randomResponse[index],
								done: false,
							});
							index++;
							setTimeout(streamNext, 30); // 30ms delay for realistic streaming
						} else {
							controller.enqueue({
								content: "",
								done: true,
							});
							controller.close();
						}
					};

					streamNext();
				},
			});
		}
	}

	// For non-streaming responses (fallback)
	async sendMessage(messages: ChatMessage[]): Promise<ChatMessage> {
		try {
			// Use the API service for non-streaming
			const response = await apiService.post<ChatMessage>(
				"/api/v1/chat/message",
				{ messages }
			);
			return response;
		} catch (error) {
			console.error("Error with real API, falling back to mock:", error);

			// Fallback to mock response if API fails
			const lastMessage = messages[messages.length - 1];
			const mockResponse = `This is a mock response to: "${lastMessage.content}". In a real application, this would be generated by your AI model.`;

			return {
				id: Date.now().toString(),
				role: "agent",
				content: mockResponse,
				timestamp: new Date().toLocaleTimeString(),
			};
		}
	}
}

export const chatApi = new ChatApiService();
